import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Home,
  Key,
  FileText,
  Gavel,
  Building2,
  HandshakeIcon,
  MessageCircle,
  CheckCircle2,
  AlertTriangle,
  ChevronDown,
  HelpCircle,
  Calculator,
} from "lucide-react";
import CalculadoraEmolumentos from "@/components/CalculadoraEmolumentos";

const LOGO_ICON = "https://customer-assets.emergentagent.com/job_direito-trabalho-sp/artifacts/8b5v8061_IconOnly_Transparent_NoBuffer.png";
const WHATSAPP_LINK = "https://wa.me/5511917611575?text=Olá! Gostaria de uma consulta sobre Direito Imobiliário.";

const ImobiliarioPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      pergunta: "O que é usucapião e quais os requisitos?",
      resposta: "Usucapião é a forma de adquirir a propriedade de um imóvel pela posse prolongada e ininterrupta. Os requisitos variam conforme a modalidade: usucapião extraordinária (15 anos de posse), ordinária (10 anos com justo título e boa-fé), especial urbana (5 anos em imóvel de até 250m²) ou rural (5 anos em área de até 50 hectares)."
    },
    {
      pergunta: "Comprei um imóvel e descobri problemas estruturais. O que fazer?",
      resposta: "Você pode exigir do vendedor a reparação dos vícios ocultos (defeitos que não eram visíveis na compra). O prazo para reclamar é de 1 ano a partir da descoberta do problema. Em casos graves, é possível pedir a rescisão do contrato e devolução dos valores pagos."
    },
    {
      pergunta: "Como funciona a rescisão de contrato de compra de imóvel na planta?",
      resposta: "Conforme a Lei do Distrato (13.786/2018), se o comprador desistir, a incorporadora pode reter até 25% dos valores pagos (ou até 50% se o imóvel integrar patrimônio de afetação). A restituição deve ocorrer em até 180 dias após a rescisão."
    },
    {
      pergunta: "O que é alienação fiduciária de imóvel?",
      resposta: "É uma garantia em que o comprador transfere a propriedade do imóvel ao credor até a quitação do financiamento. Se houver inadimplência, o credor pode consolidar a propriedade e leiloar o bem, de forma mais rápida que a hipoteca tradicional."
    },
    {
      pergunta: "Posso ser despejado se atrasar o aluguel?",
      resposta: "Sim. O locador pode ingressar com ação de despejo por falta de pagamento. Porém, o inquilino tem a opção de purgar a mora (pagar os aluguéis atrasados + encargos) até a sentença para evitar o despejo. Após a sentença, o prazo para desocupação é de 15 a 30 dias."
    },
    {
      pergunta: "Preciso de advogado para comprar um imóvel?",
      resposta: "Não é obrigatório, mas é altamente recomendado. O advogado analisa a documentação do imóvel e do vendedor, verifica a existência de dívidas, ações judiciais ou ônus que possam afetar a compra, e elabora ou revisa o contrato para proteger seus interesses."
    },
  ];

  const servicos = [
    { icon: <Home className="w-5 h-5" />, title: "Compra e Venda", desc: "Análise de documentação e elaboração de contratos seguros" },
    { icon: <Key className="w-5 h-5" />, title: "Locação", desc: "Contratos de aluguel, despejo e cobrança de aluguéis" },
    { icon: <FileText className="w-5 h-5" />, title: "Usucapião", desc: "Regularização de propriedade por posse prolongada" },
    { icon: <Gavel className="w-5 h-5" />, title: "Ações Possessórias", desc: "Reintegração e manutenção de posse" },
    { icon: <Building2 className="w-5 h-5" />, title: "Condomínios", desc: "Cobrança de taxas e questões condominiais" },
    { icon: <HandshakeIcon className="w-5 h-5" />, title: "Inventário", desc: "Divisão de bens imobiliários em heranças" },
  ];

  return (
    <>
      <Helmet>
        <title>Advogado Imobiliário | Helio Lopes Leal Advocacia</title>
        <meta name="description" content="Advogado imobiliário especializado. Assessoria em compra e venda, contratos, regularização e disputas imobiliárias." />
        <meta name="keywords" content="advogado imobiliário, direito imobiliário, usucapião, compra e venda imóveis" />
      </Helmet>

      <div>
        {/* Hero */}
        <section className="pt-32 pb-20 bg-gradient-to-br from-amber-50 to-white relative overflow-hidden">
          <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-[0.03] pointer-events-none">
            <img src={LOGO_ICON} alt="" className="w-[500px] h-auto" style={{ filter: "brightness(0)" }} />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <p className="text-amber-700 font-medium tracking-wider text-sm mb-4">DIREITO IMOBILIÁRIO</p>
            <h1
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 max-w-3xl"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Advogado <span className="text-amber-700">Imobiliário</span>
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mb-8">
              Assessoria em compra e venda de imóveis, análise e elaboração de contratos, 
              regularização imobiliária e atuação em disputas possessórias e contratuais.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-amber-700 hover:bg-amber-800 text-white px-8 py-4 rounded font-medium transition-all">
                <MessageCircle className="w-5 h-5" />
                Fale com Especialista
              </a>
              <Link to="/contato"
                className="inline-flex items-center justify-center border-2 border-gray-300 hover:border-amber-700 text-gray-700 hover:text-amber-700 px-8 py-4 rounded font-medium transition-all">
                Enviar Mensagem
              </Link>
            </div>
          </div>
        </section>

        {/* Quando procurar */}
        <section className="py-16 bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Quando Procurar um Advogado Imobiliário?
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Antes de comprar ou vender um imóvel, ao assinar um contrato de locação, 
                para regularizar a documentação de um imóvel, em disputas de posse ou 
                questões condominiais. A assessoria jurídica preventiva evita problemas futuros.
              </p>
            </div>
          </div>
        </section>

        {/* Serviços */}
        <section className="py-20 bg-gray-50 relative overflow-hidden">
          <div className="absolute left-0 bottom-0 opacity-[0.02] pointer-events-none">
            <img src={LOGO_ICON} alt="" className="w-[400px] h-auto" style={{ filter: "brightness(0)" }} />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-10" style={{ fontFamily: "'Playfair Display', serif" }}>
              Nossos Serviços Imobiliários
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {servicos.map((servico, index) => (
                <div key={index} className="bg-white p-6 rounded-lg border border-gray-100 hover:shadow-md transition-all">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center text-amber-700 mb-4">
                    {servico.icon}
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">{servico.title}</h4>
                  <p className="text-gray-600 text-sm">{servico.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Info Box */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-8 max-w-4xl mx-auto">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-8 h-8 text-amber-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    Por que contar com um advogado imobiliário?
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Transações imobiliárias envolvem valores significativos e documentação complexa. 
                    Um erro pode resultar em prejuízos financeiros ou até perda do imóvel.
                  </p>
                  <ul className="space-y-2">
                    {[
                      "Análise de matrícula e certidões",
                      "Verificação de ônus e gravames",
                      "Elaboração de contratos seguros",
                      "Acompanhamento em cartórios",
                    ].map((item, index) => (
                      <li key={index} className="flex items-center gap-2 text-gray-600">
                        <CheckCircle2 className="w-5 h-5 text-amber-600" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Calculadora de Emolumentos */}
        <section className="py-20 bg-gray-50 relative overflow-hidden">
          <div className="absolute right-0 top-0 opacity-[0.02] pointer-events-none">
            <img src={LOGO_ICON} alt="" className="w-[400px] h-auto" style={{ filter: "brightness(0)" }} />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <Calculator className="w-6 h-6 text-amber-700" />
                  <p className="text-amber-700 font-medium tracking-wider text-sm">FERRAMENTA ÚTIL</p>
                </div>
                <h2 
                  className="text-2xl md:text-3xl font-bold text-gray-900 mb-6"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Calcule o Custo do Registro
                </h2>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Utilize nossa calculadora para estimar os custos de registro ou averbação 
                  de imóveis no Estado de São Paulo. Os valores são baseados na tabela oficial 
                  de emolumentos (ARISP).
                </p>
                <div className="space-y-4 text-sm text-gray-600">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p><strong>Registro:</strong> Ato que transfere ou constitui direitos reais sobre o imóvel (compra e venda, hipoteca, etc.)</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p><strong>Averbação:</strong> Anotações que modificam o registro existente (construção, demolição, alteração de estado civil, etc.)</p>
                  </div>
                </div>
              </div>
              <div>
                <CalculadoraEmolumentos />
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <HelpCircle className="w-10 h-10 text-amber-700 mx-auto mb-4" />
              <h2 
                className="text-2xl md:text-3xl font-bold text-gray-900 mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Perguntas Frequentes
              </h2>
              <p className="text-gray-600">
                Tire suas principais dúvidas sobre Direito Imobiliário
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div 
                  key={index}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-5 text-left bg-white hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-medium text-gray-900 pr-4">{faq.pergunta}</span>
                    <ChevronDown 
                      className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform ${
                        openFaq === index ? "rotate-180" : ""
                      }`} 
                    />
                  </button>
                  {openFaq === index && (
                    <div className="px-5 pb-5 pt-0">
                      <p className="text-gray-600 leading-relaxed">{faq.resposta}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-amber-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Vai Comprar, Vender ou Regularizar um Imóvel?
            </h2>
            <p className="text-amber-100 mb-8 max-w-xl mx-auto">
              Garanta segurança jurídica em sua transação imobiliária.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded font-medium transition-all">
                <MessageCircle className="w-5 h-5" />
                Falar pelo WhatsApp
              </a>
              <Link to="/contato"
                className="inline-flex items-center justify-center border-2 border-white/30 hover:border-white text-white px-8 py-4 rounded font-medium transition-all">
                Enviar Mensagem
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ImobiliarioPage;
